import React from 'react';
import PropTypes from 'prop-types';
import { Visibility } from '@reservamos/elements';
import QuickPurchaseAvailableBanner from '../../../ui/atoms/QuickPurchaseAvailableBanner';
import SpecialDiscountBanner from '../../../ui/atoms/SpecialDiscountBanner';
import WelcomeDigitalDiscountModal from '../ModalContent/WelcomeDigitalDiscountModal';

/**
 * Resolve which single banner type should show on the results / recommended trips UI.
 * Quick purchase takes priority over the welcome digital discount banner.
 *
 * @param {Array|undefined|null} searchTrips - Search result trips (used only when quick purchase is enabled).
 * @param {boolean} showWelcomeDigitalDiscountBanner - Whether welcome banner is enabled.
 * @param {boolean} quickPurchaseEnabled - Same gate as {@link QuickPurchaseAvailableBanner} (QUICK_PURCHASE_ENABLED).
 * @param {boolean} showSpecialDiscountBanner - Whether special discount banner is enabled.
 * @returns {'specialDiscount'|'quick'|'welcome'|'none'} Resolved banner kind.
 */
export const resolveRecommendedTripsBanner = (
  searchTrips,
  showWelcomeDigitalDiscountBanner,
  quickPurchaseEnabled,
  showSpecialDiscountBanner,
) => {
  if (showSpecialDiscountBanner) {
    return 'specialDiscount';
  }
  if (quickPurchaseEnabled && Array.isArray(searchTrips) && searchTrips.length > 0) {
    return 'quick';
  }
  if (showWelcomeDigitalDiscountBanner) return 'welcome';
  return 'none';
};

/**
 * ResultsBannerSlot component.
 * Single decision (quick vs welcome vs none) per render; each variant maps that decision to
 * the original recommended-trips DOM slots without showing both banners.
 *
 * @param {object} props - searchTrips, isRoundTrip, isExchange, quickPurchaseEnabled, showWelcomeDigitalDiscountBanner, showSpecialDiscountBanner, specialDiscountVariant, variant.
 * @param {Array|undefined|null} props.searchTrips - Trips passed to quick purchase banner.
 * @param {boolean} props.isRoundTrip - Round-trip flag for quick purchase.
 * @param {boolean} props.isExchange - Exchange flow flag for quick purchase.
 * @param {boolean} props.quickPurchaseEnabled - Quick purchase feature flag (must match banner visibility).
 * @param {boolean} props.showWelcomeDigitalDiscountBanner - Welcome banner visibility flag.
 * @param {boolean} props.showSpecialDiscountBanner - Special discount banner visibility flag.
 * @param {'etn'|'gho'|'costa'|'gfa'|'rochoa'} props.specialDiscountVariant - Brand variant for the special discount banner.
 * @param {'recommendedTripsEmpty'|'recommendedTripsHeaderMobile'|'recommendedTripsHeaderAside'} props.variant - DOM slot.
 * @param {Function} props.onBannerClick - Called when the quick purchase banner is clicked.
 *
 * @returns {JSX.Element|null} Content for this slot, or null.
 */
const ResultsBannerSlot = ({
  searchTrips,
  isRoundTrip,
  isExchange,
  quickPurchaseEnabled,
  showWelcomeDigitalDiscountBanner,
  showSpecialDiscountBanner,
  specialDiscountVariant,
  variant,
  onBannerClick,
}) => {
  const resolved = resolveRecommendedTripsBanner(
    searchTrips,
    showWelcomeDigitalDiscountBanner,
    quickPurchaseEnabled,
    showSpecialDiscountBanner,
  );

  if (resolved === 'none') {
    return null;
  }

  /**
   * Render QuickPurchaseAvailableBanner for the resolved quick-purchase state.
   *
   * @returns {JSX.Element} Quick purchase banner.
   */
  const renderQuickPurchaseBanner = () => (
    <QuickPurchaseAvailableBanner
      trips={searchTrips}
      isRoundTrip={Boolean(isRoundTrip)}
      isExchange={Boolean(isExchange)}
      onClick={onBannerClick}
    />
  );

  /**
   * Render special discount banner based on brand variant.
   *
   * @returns {JSX.Element} Special discount banner for the current brand.
   */
  const renderSpecialDiscountBanner = () => (
    <SpecialDiscountBanner variant={specialDiscountVariant} />
  );

  /**
   * Render welcome discount modal for the results screen.
   *
   * @returns {JSX.Element} Welcome digital discount modal.
   */
  const renderWelcomeModal = (useTagShapeMobile = false) => (
    <WelcomeDigitalDiscountModal screen="results" useTagShapeMobile={useTagShapeMobile} />
  );

  if (variant === 'recommendedTripsEmpty') {
    const bannerMap = {
      specialDiscount: renderSpecialDiscountBanner,
      quick: renderQuickPurchaseBanner,
      welcome: renderWelcomeModal,
    };

    return <div className="flex flex-row justify-end gap-3">{bannerMap[resolved]()}</div>;
  }

  if (variant === 'recommendedTripsHeaderMobile') {
    if (resolved === 'specialDiscount') {
      return <div className="show-for-mobile-only mb-4">{renderSpecialDiscountBanner()}</div>;
    }
    if (resolved === 'quick') {
      return <div className="show-for-mobile-only mb-4">{renderQuickPurchaseBanner()}</div>;
    }

    return <div className="show-for-mobile-only mb-4">{renderWelcomeModal(true)}</div>;
  }

  if (variant === 'recommendedTripsHeaderAside') {
    if (resolved === 'specialDiscount') {
      return <Visibility type="hideForMobileOnly">{renderSpecialDiscountBanner()}</Visibility>;
    }
    if (resolved === 'quick') {
      return (
        <div className="flex flex-col-reverse gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div />
          <Visibility type="hideForMobileOnly">{renderQuickPurchaseBanner()}</Visibility>
        </div>
      );
    }

    return (
      <div className="flex flex-col-reverse gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div />
        <Visibility type="hideForMobileOnly">{renderWelcomeModal()}</Visibility>
      </div>
    );
  }

  return null;
};

ResultsBannerSlot.propTypes = {
  searchTrips: PropTypes.array,
  isRoundTrip: PropTypes.bool,
  isExchange: PropTypes.bool,
  quickPurchaseEnabled: PropTypes.bool.isRequired,
  showWelcomeDigitalDiscountBanner: PropTypes.bool.isRequired,
  showSpecialDiscountBanner: PropTypes.bool,
  specialDiscountVariant: PropTypes.oneOf(['etn', 'gho', 'costa', 'gfa', 'rochoa']),
  onBannerClick: PropTypes.func,
  variant: PropTypes.oneOf([
    'recommendedTripsEmpty',
    'recommendedTripsHeaderMobile',
    'recommendedTripsHeaderAside',
  ]).isRequired,
};

ResultsBannerSlot.defaultProps = {
  showSpecialDiscountBanner: false,
  specialDiscountVariant: 'gho',
};

export default ResultsBannerSlot;
